/* eslint-disable no-inline-comments */
import {
  useState,
  useRef,
  FC,
} from 'react';

import {
  successToast,
  errorToast,
} from 'utils/toast';
import { tailwindInlineStyles } from 'utils/tailwindInlineStyles';

export interface IWithPrintPDFComponentProps {
  handlePrint: () => void;
  setDocumentTitle: (title?: string) => void;
  isPDF: boolean;
  printLoading: boolean;
}

const withPrintPDF = (Component: FC<any>) => function (props: React.ComponentProps<typeof Component>) {
  const targetRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [documentTitle, setDocumentTitle] = useState('');
  const reactNativeWebView = (window as any)?.ReactNativeWebView;

  // const handlePrint = useReactToPrint({
  //   content: () => targetRef.current,
  //   documentTitle,
  //   onBeforePrint: () => {
  //     setLoading(true);
  //   },
  //   onAfterPrint: () => {
  //     setLoading(false);
  //   },
  //   onPrintError: (location, error) => {
  //     errorToast(location);
  //     errorToast(error.message);
  //     setLoading(false);
  //   },
  //   pageStyle: `
  //    @page {
  //     margin: 16px;
  //     size: A3;
  //    }
  //    body {
  //     background-color: #fff;
  //     -webkit-print-color-adjust: exact; /* Ensure colors are printed accurately */
  //     width: 100%;
  //     padding: 0;
  //     margin: 16px auto;
  //    }
  //     img {
  //       max-width: 100%; /* Responsive images */
  //       height: auto;
  //     }
  //   `,
  // });

  const handleDownloadPDF = async () => {
    const element = targetRef.current;

    if (!element) {
      errorToast('Target element is null');

      return;
    }

    setLoading(true);

    try {
      const html2pdf = (await import('html2pdf.js')).default;

      // Clone and prepare the element
      const clonedElement = element.cloneNode(true) as HTMLElement;

      // Apply styles directly to prevent gaps
      clonedElement.style.margin = '0';
      clonedElement.style.padding = '0';

      // Remove all large margins from children
      const allElements = clonedElement.querySelectorAll('*');

      allElements.forEach((el) => {
        const htmlEl = el as HTMLElement;

        // Remove large top margins
        if (htmlEl.classList.contains('mt-24') || 
            htmlEl.classList.contains('sm:mt-[250px]') ||
            htmlEl.classList.contains('mt-20') ||
            htmlEl.classList.contains('mt-16')) {
          htmlEl.style.marginTop = '0';
        }

        // Check computed styles
        const computedStyle = window.getComputedStyle(htmlEl);
        const marginTop = computedStyle.marginTop;

        if (marginTop && (parseInt(marginTop) > 50)) {
          htmlEl.style.marginTop = '0';
        }
      });

      const opt = {
        margin: [0.3, 0.3, 0.3, 0.3] as [number, number, number, number],
        filename: `${documentTitle || 'document'}.pdf`,
        image: {
          type: 'jpeg' as const,
          quality: 0.95,
        },
        html2canvas: {
          scale: 1.8,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          windowWidth: 1600,
          width: 1600,
          scrollY: -window.scrollY,
          scrollX: 0,
          allowTaint: false,
          removeContainer: true,
          imageTimeout: 0,
          onclone: (clonedDoc: Document) => {
            // Force desktop view by overriding responsive styles
            const style = clonedDoc.createElement('style');

            style.textContent = `
              @media print {
                * {
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
              }
              /* Force desktop layout with proper width */
              body {
                min-width: 1600px !important;
                width: 1600px !important;
                margin: 0 !important;
                padding: 0 !important;
              }
              .pdfDoc {
                min-width: 1600px !important;
                width: 1600px !important;
                margin: 0 !important;
                padding: 0 !important;
              }
              /* Remove ALL large margins */
              .mt-24,
              .mt-20,
              .mt-16,
              .mt-12 {
                margin-top: 0 !important;
              }
              .sm\\:mt-\\[250px\\],
              .sm\\:mt-\\[200px\\],
              .sm\\:mt-\\[150px\\],
              .md\\:mt-\\[250px\\],
              .md\\:mt-\\[200px\\],
              .md\\:mt-\\[150px\\] {
                margin-top: 0 !important;
              }
              /* Force 4-column grid layout for summary section */
              .grid {
                display: grid !important;
                grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
                gap: 0.75rem !important;
              }
              /* Override ALL responsive grid classes to use 4 columns */
              .grid-cols-1,
              .grid-cols-2,
              .grid-cols-3,
              .grid-cols-4,
              .grid-cols-5,
              .grid-cols-6,
              .md\\:grid-cols-1,
              .md\\:grid-cols-2,
              .md\\:grid-cols-3,
              .md\\:grid-cols-4,
              .xl\\:grid-cols-4,
              .xl\\:grid-cols-6,
              .2xl\\:grid-cols-4,
              .2xl\\:grid-cols-6,
              .3xl\\:grid-cols-4,
              .3xl\\:grid-cols-6 {
                grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
              }
              /* Remove all mobile-specific padding and margins */
              .md\\:px-\\[16px\\],
              .md\\:px-4 {
                padding-left: 1rem !important;
                padding-right: 1rem !important;
              }
              .md\\:py-3 {
                padding-top: 1rem !important;
                padding-bottom: 1rem !important;
              }
              .md\\:text-sm { font-size: 0.875rem !important; }
              .md\\:text-lg { font-size: 1.125rem !important; }
              .md\\:mt-6 { margin-top: 1.5rem !important; }
              .md\\:mt-4 { margin-top: 1rem !important; }
              .md\\:mt-2 { margin-top: 0.5rem !important; }
              .md\\:mb-4 { margin-bottom: 1rem !important; }
              .md\\:mb-8 { margin-bottom: 2rem !important; }
              .sm\\:flex-col-reverse { flex-direction: row !important; }
              .sm\\:w-full { width: auto !important; }
              .sm\\:flex-auto { flex: initial !important; }
              /* Fix tabbar */
              .tabbar-container {
                position: relative !important;
                top: 0 !important;
                transform: none !important;
                box-shadow: none !important;
                padding: 16px !important;
              }
              /* Ensure proper alignment and padding for inputs */
              select, input {
                vertical-align: middle !important;
                width: 100% !important;
                padding: 0.625rem 0.75rem !important;
                min-height: 2.75rem !important;
                line-height: 1.5 !important;
                box-sizing: border-box !important;
              }
              /* Fix select dropdown appearance */
              select {
                padding-right: 2.5rem !important;
                background-position: right 0.75rem center !important;
                background-size: 1rem !important;
                appearance: none !important;
                -webkit-appearance: none !important;
                -moz-appearance: none !important;
                display: block !important;
              }
              /* Fix react-select and custom dropdown components */
              [class*="select"],
              [class*="Select"],
              [class*="dropdown"] {
                min-height: 2.75rem !important;
              }
              [class*="select"] > div,
              [class*="Select"] > div,
              [class*="control"] {
                min-height: 2.75rem !important;
                padding: 0.5rem 0.75rem !important;
                display: flex !important;
                align-items: center !important;
              }
              /* Fix dropdown indicator/arrow */
              [class*="indicatorContainer"],
              [class*="IndicatorContainer"],
              [class*="indicator"] {
                padding: 0.5rem !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
              }
              /* Fix dropdown value text */
              [class*="singleValue"],
              [class*="SingleValue"],
              [class*="placeholder"] {
                line-height: 1.5 !important;
                margin: 0 !important;
                padding: 0 !important;
              }
              /* Ensure labels have proper spacing */
              label {
                margin-bottom: 0.375rem !important;
                display: block !important;
              }
              /* Fix font-bold class margin */
              .font-bold {
                margin-bottom: 0.375rem !important;
              }
              /* Specific fix for summary card titles */
              .p-2 .font-bold {
                margin-bottom: 0.5rem !important;
                padding-bottom: 0.25rem !important;
              }
              /* Fix summary section cards padding */
              .p-2 {
                padding: 0.5rem !important;
                padding-bottom: 1rem !important;
                min-height: 4rem !important;
                margin-top: 0 !important;
              }
              .\\33xl\\:bg-gray-50,
              .\\32xl\\:bg-gray-50 {
                padding: 0.5rem !important;
                padding-bottom: 1rem !important;
                background-color: #f9fafb !important;
                margin-top: 0 !important;
              }
              /* Fix mb-1.5 class that might be overridden */
              .mb-1\\.5,
              .mb-1 {
                margin-bottom: 0.5rem !important;
              }
              /* More specific override for summary card titles */
              .p-2 .mb-1\\.5 {
                margin-bottom: 0.5rem !important;
                padding-bottom: 0.25rem !important;
              }
              /* Ensure section titles have proper spacing */
              .text-black.font-bold.text-xl,
              .text-black.font-bold.md\\:text-lg {
                margin-top: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              /* Ensure block titles have spacing */
              .mb-4 {
                margin-bottom: 1rem !important;
              }
              .mb-6 {
                margin-bottom: 1.5rem !important;
              }
              .mb-8 {
                margin-bottom: 2rem !important;
              }
              /* Prevent text from being cut off */
              * {
                overflow: visible !important;
              }
              /* Ensure proper line height for all text */
              body, div, p, span, label {
                line-height: 1.5 !important;
              }
              /* Fix container widths */
              .max-w-\\[1700px\\] {
                max-width: 1600px !important;
                margin-left: auto !important;
                margin-right: auto !important;
              }
              /* Override container padding */
              .px-\\[100px\\] {
                padding-left: 2rem !important;
                padding-right: 2rem !important;
              }
              .py-\\[32px\\],
              .p-\\[32px\\] {
                padding-top: 2rem !important;
                padding-bottom: 2rem !important;
              }
              /* Ensure content doesn't overflow */
              * {
                box-sizing: border-box !important;
              }
              /* Fix background colors for summary cards */
              .\\33xl\\:bg-gray-50,
              .\\32xl\\:bg-gray-50 {
                background-color: #f9fafb !important;
              }
              .\\33xl\\:rounded-lg,
              .\\32xl\\:rounded-lg {
                border-radius: 0.5rem !important;
              }
            `;
            clonedDoc.head.appendChild(style);

            // Directly remove margin from elements with large top margins
            const elementsWithMargin = clonedDoc.querySelectorAll('.mt-24, .sm\\:mt-\\[250px\\]');

            elementsWithMargin.forEach((el) => {
              (el as HTMLElement).style.marginTop = '0';
            });

            // Also check for any div with inline styles that have large margins
            const allDivs = clonedDoc.querySelectorAll('div');

            allDivs.forEach((div) => {
              const htmlDiv = div as HTMLElement;
              const computedMargin = htmlDiv.style.marginTop;

              if (computedMargin && (computedMargin.includes('250px') || computedMargin.includes('24rem') || computedMargin.includes('96px'))) {
                htmlDiv.style.marginTop = '0';
              }
            });

            // Ensure the element is visible in the cloned document
            const pdfElement = clonedDoc.querySelector('.pdfDoc') as HTMLElement;

            if (pdfElement) {
              pdfElement.style.display = 'block';
              pdfElement.style.visibility = 'visible';
              pdfElement.style.opacity = '1';
              pdfElement.style.width = '1600px';
              pdfElement.style.maxWidth = '1600px';
            }
          },
        },
        jsPDF: {
          unit: 'in',
          format: 'a3',
          orientation: 'portrait' as const,
          compress: true,
        },
        pagebreak: {
          mode: [],
          before: [],
          after: [],
          avoid: [],
        },
      };

      // Use worker API for better control
      const worker = html2pdf().set(opt).from(clonedElement);

      await worker.save();

      setLoading(false);
      successToast('PDF downloaded successfully');
    } catch (error) {
      setLoading(false);
      errorToast('Error generating PDF');
      console.error('Error generating PDF:', error);
    }
  };

  const handlePrintMobile = async () => {
    function convertUrlToLocalPath(url: string) {
      try {
        if (url.startsWith('blob:')) {
          return url; // Return the original blob URL as is
        }

        const parsedUrl = new URL(url);

        return parsedUrl.pathname;
      } catch (error) {
        console.error(`Error parsing URL: ${url}`, error);

        return url; // Return the original URL if parsing fails
      }
    }

    const element = targetRef.current;

    if (!element) {
      errorToast('Target element is null');

      return;
    }

    // Start loading
    setLoading(true);

    try {
      // Convert the target element to HTML string
      const htmlString = element.innerHTML;

      // Include the Tailwind CSS in the head of the HTML

      // Check for images and convert them to base64 if necessary
      const images = Array.from(element.querySelectorAll('img'));

      const imagePromises = images.map(async (img) => {
        const src = img.src;

        if (src.startsWith('blob:') || src.startsWith('http')) {
          try {
            // Fetch the image and convert to base64
            const response = await fetch(src);
            const blob = await response.blob();
            const reader = new FileReader();

            return new Promise((resolve) => {
              reader.onloadend = () => {
                resolve(reader.result); // Return base64 string
              };
              reader.readAsDataURL(blob);
            });
          } catch (error) {
            console.error(`Error fetching image: ${src}`, error);

            return ''; // Set to empty string if there's an error
          }
        } else if (src.endsWith('.jpg')) {
          // Handle images with local .jpg format
          try {
            const response = await fetch(`${window.location.origin}${src}`);
            const blob = await response.blob();
            const reader = new FileReader();

            return new Promise((resolve) => {
              reader.onloadend = () => {
                resolve(reader.result); // Return base64 string for local image
              };
              reader.readAsDataURL(blob);
            });
          } catch (error) {
            console.error(`Error fetching local .jpg image: ${src}`, error);

            return ''; // Set to empty string if there's an error
          }
        }

        return src; // Return the original src if it's not a blob or .jpg URL
      });

      // Wait for all images to be converted to base64
      const base64Images = await Promise.all(imagePromises);

      // Replace original image sources in the HTML string
      let updatedHtmlString = htmlString;
      base64Images.forEach((base64Image, index) => {
        const originalSrc = images[index].src; // Keep track of the original src

        const imgTagRegex = new RegExp(
          `<img[^>]*src="${convertUrlToLocalPath(originalSrc)}"[^>]*>`,
        );

        // Replace with base64 or empty src if the image conversion failed
        updatedHtmlString = updatedHtmlString.replace(
          imgTagRegex,
          `<img src="${base64Image}" alt="Image ${index + 1}" />`,
        );
      });

      // Create a complete HTML document with Tailwind styles
      const htmlContent = `
          <html>
            <head>
              <style>
                @page {
                  margin: 16px;
                  size: A3;
                }
                body {
                  background-color: #fff;
                  -webkit-print-color-adjust: exact; /* Ensure colors are printed accurately */
                  width: 100%;
                  padding: 0;
                  margin: 16px auto;
                }
                img {
                  max-width: 100%; /* Responsive images */
                  height: auto;
                }
                
                ${tailwindInlineStyles}
              </style>
            </head>
            <body>
              ${updatedHtmlString}
            </body>
          </html>
        `;
      // Send the complete HTML content back to the React Native app using postMessage
      reactNativeWebView.postMessage(
        JSON.stringify({
          type: 'html',
          htmlString: htmlContent, // Send the complete HTML content
          filename: documentTitle, // Change filename to reflect that it's HTML
        }),
      );

      setLoading(false);
      successToast('PDF is successfully downloaded');
    } catch (error) {
      setLoading(false);
      errorToast('Error sending HTML to WebView');
      console.error('Error sending HTML to WebView', error);
    }
  };

  const onHandlePrint = () => {
    if (reactNativeWebView) {
      handlePrintMobile();
    } else {
      handleDownloadPDF();
    }
  };

  return (
    <>
      <div
        style={{
          position: 'absolute',
          left: '-99999px',
          top: 0,
          width: '1600px',
          minWidth: '1600px',
          maxWidth: '1600px',
          backgroundColor: '#ffffff',
        }}
      >
        <div ref={targetRef} className="pdfDoc">
          <Component isPDF {...props} />
        </div>
      </div>
      <Component
        handlePrint={onHandlePrint}
        printLoading={loading}
        setDocumentTitle={setDocumentTitle}
        {...props}
      />
    </>
  );
};

export default withPrintPDF;
